import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { SignUpStatus, TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useConfiguration } from "@is/hooks";
import { useSignUp } from "@is/hooks/useSignUp";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  FieldsValidation,
  SignUpFormState,
  SignUpInputFields,
} from "@is/types";
import {
  INPUT_EMAIL_REGEX,
  getTranslatedValue,
  toCamelCase,
  validateCheckoutField,
} from "@is/utils";

type ErrorState = {
  [key: string]: {
    error: boolean;
    message: string;
  };
};

const useManageSignUpModal = () => {
  const { t } = useTranslation(TranslationContext.General);

  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [isAgreementRequired, setIsAgreementRequired] = useState(false);
  const [formState, setFormState] = useState<SignUpFormState>({});
  const [fieldsValidation, setFieldsValidation] = useState<ErrorState>(null);

  const { loyaltySignupForm } = useConfiguration();
  const [{ error, isPending }, signUp] = useSignUp();

  const { updateViewState } = useContext(ViewStateContext).content;

  const title = useMemo(
    () =>
      getTranslatedValue(loyaltySignupForm.title.translations) ??
      loyaltySignupForm?.title?.value ??
      "",
    [loyaltySignupForm]
  );

  const description = useMemo(
    () =>
      getTranslatedValue(loyaltySignupForm?.description?.translations) ??
      loyaltySignupForm?.description?.value ??
      "",
    [loyaltySignupForm]
  );

  const agreement = useMemo(
    () =>
      getTranslatedValue(loyaltySignupForm?.agreement?.translations) ??
      loyaltySignupForm?.agreement?.value ??
      "",
    [loyaltySignupForm.agreement]
  );

  const isConfirmButtonDisabled = useMemo(() => {
    const requiredFields = Object.values(formState).filter(
      (field) => field.isRequired
    );

    const allRequiredFieldsHaveValue = requiredFields.every(
      (field) => !!field.value.length
    );

    return (
      (isAgreementRequired && !isAgreementChecked) ||
      !allRequiredFieldsHaveValue
    );
  }, [formState, isAgreementChecked, isAgreementRequired]);

  const getFieldsValidation = useCallback(
    () =>
      Object.entries(formState).reduce((acc: FieldsValidation, fieldState) => {
        const [field, { value, validationRegex, maxLength }] = fieldState;

        let invalid = false;
        let errorMessage = "";

        if (!invalid && value && validationRegex) {
          const regex = new RegExp(validationRegex);
          invalid = !regex.test(value);
          errorMessage = invalid ? t("Invalid value") : "";
        }

        if (!invalid && maxLength) {
          invalid = value.length > maxLength;
          if (invalid) {
            errorMessage = t(`Maximum {{maxLength}} symbols`, {
              maxLength,
            });
          }
        }

        return {
          ...acc,
          [field]: {
            error: invalid,
            message: errorMessage,
          },
        };
      }, {}),
    [formState, t]
  );

  const handleFormFieldChange = useCallback(
    (name: SignUpInputFields, type: string, value: string) => {
      const validatedValue = validateCheckoutField(
        name as SignUpInputFields,
        type,
        value
      );

      setFormState((prev) => ({
        ...prev,
        [name]: {
          ...prev[name],
          value: validatedValue,
        },
      }));

      setFieldsValidation((prev) => ({
        ...prev,
        [name]: {
          error: false,
          message: "",
        },
      }));
    },
    []
  );

  const handleCheckedToggle = useCallback(() => {
    setIsAgreementChecked((prev) => !prev);
  }, []);

  const handleConfirm = useCallback(async () => {
    const validatedFields = getFieldsValidation();

    const notValidFields = Object.values(validatedFields).filter(
      (field) => field.error
    );

    if (notValidFields.length) {
      setFieldsValidation(validatedFields);

      return;
    }

    const properties = Object.values(formState).map((field) => ({
      id: field.id,
      value: field.value,
    }));

    const filteredProperties = properties.filter(
      (field) => !!field.value.length
    );

    const agreements = [
      {
        isRequired: isAgreementRequired,
        isChecked: isAgreementChecked,
      },
    ];

    trackEvent({
      eventType: TrackingEventTypes.SignUpConfirm,
      data: {
        properties: filteredProperties.map((field) => ({ id: field.id })),
        agreements: agreements.map((agreement) => ({
          isRequired: agreement.isRequired,
          isChecked: agreement.isChecked,
        })),
      },
    });

    const result = await signUp({
      properties: filteredProperties,
      agreements,
    });

    if (result) {
      trackEvent({
        eventType: TrackingEventTypes.SignUpConfirmSuccess,
        data: {},
      });

      setFormState({});
      setFieldsValidation(null);

      updateViewState({
        showSignUpModal: false,
        signUpStatusModal: {
          isOpen: true,
          status: SignUpStatus.AccountCreated,
        },
      });
    }
  }, [
    formState,
    getFieldsValidation,
    isAgreementChecked,
    isAgreementRequired,
    signUp,
    updateViewState,
  ]);

  const handleCancel = useCallback(() => {
    updateViewState({ showSignUpModal: false });
  }, [updateViewState]);

  const handleLinkClick = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      const link = e.currentTarget.attributes["href"].value;

      updateViewState({
        externalPageDrawer: { isOpen: true, src: link },
      });
    },
    [updateViewState]
  );

  useEffect(() => {
    if (loyaltySignupForm?.personalInformation) {
      const enabledFields = loyaltySignupForm?.personalInformation?.filter(
        (field) => field.isEnabled
      );

      const initialFormState: SignUpFormState = enabledFields?.reduce(
        (acc, option) => ({
          ...acc,
          [toCamelCase(option.name)]: {
            ...option,
            name: toCamelCase(option.name),
            value: "",
            ...(toCamelCase(option.name) === SignUpInputFields.Email && {
              validationRegex: INPUT_EMAIL_REGEX,
            }),
          },
        }),
        {}
      );

      const initialErrorState = Object.keys(initialFormState).reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            error: false,
            message: "",
          },
        }),
        {}
      );

      setFormState(initialFormState);
      setFieldsValidation(initialErrorState);
    }
  }, [loyaltySignupForm?.personalInformation]);

  useEffect(() => {
    if (loyaltySignupForm?.agreement) {
      if (!agreement?.length) {
        return;
      }

      setIsAgreementRequired(loyaltySignupForm.agreement.isRequired);
    }
  }, [agreement?.length, loyaltySignupForm.agreement]);

  useEffect(() => {
    if (error) {
      trackEvent({
        eventType: TrackingEventTypes.SignUpConfirmFailed,
        data: {},
      });

      updateViewState({
        showSignUpModal: false,
        signUpStatusModal: {
          isOpen: true,
          status: SignUpStatus.AccountCreationFailed,
          errorMessage: error?.body?.error || null,
        },
      });
    }
  }, [error]);

  useEffect(() => {
    trackEvent({
      eventType: TrackingEventTypes.SignUpModalOpen,
      data: {},
    });
  }, []);

  return {
    isAgreementChecked,
    isAgreementRequired,
    isConfirmButtonDisabled,
    handleCheckedToggle,
    handleConfirm,
    handleCancel,
    formState,
    handleFormFieldChange,
    fieldsValidation,
    handleLinkClick,
    title,
    description,
    agreement,
    isPending,
    t,
  };
};

export default useManageSignUpModal;
